import { Icon, InfiniteTable, InfiniteTableColumns, InfiniteTableSortBy, Pill, Tooltip } from '@sixfold/common-ui';
import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { useTimeZone } from '../../components/date_formatting/timezone_context';
import { Tags } from '../../components/tags';
import { formatDateWithTimeZone } from '../../lib/date';
import { notEmpty, TourFailureReasonType, VisibilityIssueType } from '../../lib/graphql';
import { shipmentViewUrl } from '../../lib/util/url';
import { Routes } from '../../routes';
import { TourWarning, VisibilityIssue, VisibilityService } from '../entities';
import { createTpaButton, getMode, getStatusLabelColor } from '../utils';

// prettier-ignore
export interface Tour {
  tour_id: number;
  transport_id: string | null;
  shipper_transport_number: string | null;
  start_time: string | null;
  status: string | null;
  failureReason: {
    type: TourFailureReasonType;
  } | null;
  warnings: TourWarning[];
  mainVisibilityIssue: VisibilityIssue | null;
  visibilityServices: VisibilityService[];
  visibilitySources?: { primaryEventSource: string }[];
  activeVehicleAllocation?: { allocationDeviceType: string | null } | null;
  shipper: {
    company_id: string;
    company_name: string | null;
  } | null;
  carrier: {
    company_id: string;
    company_name: string | null;
  } | null;
  platform: {
    platform_id: string;
    platform_name: string;
  } | null;
  vehicles: {
    vehicle_id: string;
    license_plate_number: string | null;
  }[];
  stops:
    | ({
        sequence_number: number | null;
        actual_sequence_number: number | null;
        display_sequence_number: number;
        location: {
          name: string | null;
          address: {
            country: string | null;
          } | null;
        } | null;
      } | null)[]
    | null;
  tags: {
    name: string;
  }[];
  tpaProductionUrl: string | null;
  tpaTestUrl: string | null;
  tpaIntUrl: string | null;
  tpaDevUrl: string | null;
}

export interface FormattedTour {
  id: string;
  tour_id: number;
  transport_id: string | null;
  from_to: string;
  status: string;
  failureReasonType: TourFailureReasonType | null;
  warnings: string | null;
  mainVisibilityIssue: VisibilityIssueType | null;
  visibility_services: VisibilityService[];
  isMobile: boolean;
  shipper_transport_number: string;
  vehicles: { vehicle_id: string; license_plate_number: string | null }[];
  carrier_name: string;
  carrier_id: string | null;
  shipper_name: string;
  shipper_id: string | null;
  platform_name: string;
  platform_id: string | null;
  start_time: string;
  iso_start_time: string;
  tags: { name: string }[];
  tpaProductionUrl: string | null;
  tpaTestUrl: string | null;
  tpaIntUrl: string | null;
  tpaDevUrl: string | null;
}

export interface TourListProps {
  data: { tours: Tour[] };
  sortBy?: InfiniteTableSortBy<keyof FormattedTour>;
  onSortChange?: (sortBy: InfiniteTableSortBy<keyof FormattedTour>) => void;
}

const fromTo = (tour: Tour) => {
  const stops = tour.stops?.filter(notEmpty) ?? [];

  if (stops.length === 0) {
    return '[no stops]';
  }

  const from = stops[0];
  const to = stops[stops.length - 1];

  return [
    from.location?.name ?? `Stop ${from.display_sequence_number}`,
    to.location?.name ?? `Stop ${to.display_sequence_number}`,
  ].join(' - ');
};

const MOBILE_VISIBILITY_SOURCES = ['MOBILE', 'TTC_MOBILE'];

const statusKindMap: Record<string, 'success' | 'danger' | 'warning' | 'neutral' | 'info'> = {
  green: 'success',
  red: 'danger',
  yellow: 'warning',
  teal: 'info',
  grey: 'neutral',
};

export const TourList: React.FC<TourListProps> = (props) => {
  const {
    data: { tours },
    sortBy,
    onSortChange,
  } = props;

  const { timeZone } = useTimeZone();
  const formattedTours: FormattedTour[] = useMemo(
    () =>
      tours.map((tour) => ({
        id: tour.tour_id.toString(),
        tour_id: tour.tour_id,
        transport_id: tour.transport_id,
        platform_name: tour.platform?.platform_name ?? '-',
        platform_id: tour.platform?.platform_id ?? null,
        status: tour.status ?? '-',
        failureReasonType: tour.failureReason?.type ?? null,
        warnings: tour.warnings.length > 0 ? tour.warnings.map(({ type }) => type).join(', ') : null,
        mainVisibilityIssue: tour.mainVisibilityIssue?.type ?? null,
        visibility_services: tour.visibilityServices,
        isMobile:
          tour.activeVehicleAllocation?.allocationDeviceType === 'MOBILE' ||
          (tour.visibilitySources ?? []).some(({ primaryEventSource }) =>
            MOBILE_VISIBILITY_SOURCES.includes(primaryEventSource),
          ),
        vehicles: tour.vehicles,
        shipper_transport_number: tour.shipper_transport_number ?? '-',
        from_to: fromTo(tour),
        start_time: tour.start_time !== null ? formatDateWithTimeZone(tour.start_time, timeZone) : '-',
        iso_start_time: tour.start_time ?? '-',
        tags: tour.tags,
        shipper_name: tour.shipper?.company_name ?? '-',
        shipper_id: tour.shipper?.company_id ?? null,
        carrier_name: tour.carrier?.company_name ?? '-',
        carrier_id: tour.carrier?.company_id ?? null,
        tpaProductionUrl: tour.tpaProductionUrl ?? null,
        tpaTestUrl: tour.tpaTestUrl ?? null,
        tpaIntUrl: tour.tpaIntUrl ?? null,
        tpaDevUrl: tour.tpaDevUrl ?? null,
      })),
    [tours, timeZone],
  );

  const columns: InfiniteTableColumns<FormattedTour> = useMemo(
    () => [
      {
        id: 'tour_id',
        title: '#',
        width: '100px',
        minWidth: '100px',
        align: 'end' as const,
        cellContent: ({ row }) => (
          <Link to={Routes.Tour.generatePath({ tour_id: row.tour_id.toString() })}>{row.tour_id}</Link>
        ),
      },
      {
        id: 'from_to',
        title: 'From - To',
        width: '200px',
        minWidth: '200px',
        cellContent: ({ row }) => (
          <Link to={Routes.Tour.generatePath({ tour_id: row.tour_id.toString() })}>{row.from_to}</Link>
        ),
      },
      {
        id: 'status',
        title: 'Status',
        width: '100px',
        minWidth: '100px',
        cellContent: ({ row }) => (
          <Pill kind={statusKindMap[getStatusLabelColor(row.status)] ?? 'neutral'}>{row.status ?? '-'}</Pill>
        ),
      },
      {
        id: 'mainVisibilityIssue',
        title: 'Issues',
        width: '70px',
        minWidth: '70px',
        cellContent: ({ row }) => (
          <div style={{ display: 'flex', columnGap: 1, justifyContent: 'space-evenly' }}>
            {row.mainVisibilityIssue !== null && row.failureReasonType === null && (
              <Tooltip content={`Main visibility issue: ${row.mainVisibilityIssue}`}>
                <span style={{ cursor: 'pointer' }}>
                  <Icon name="small-visible" kind="warning" />
                </span>
              </Tooltip>
            )}
            {row.warnings !== null && (
              <Tooltip content={`Warnings: ${row.warnings}`}>
                <span style={{ cursor: 'pointer' }}>
                  <Icon name="small-alert" kind="warning" />
                </span>
              </Tooltip>
            )}
            {row.failureReasonType !== null && (
              <Tooltip content={`Failure reason: ${row.failureReasonType}`}>
                <span style={{ cursor: 'pointer' }}>
                  <Icon name="small-cross" kind="danger" />
                </span>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        id: 'shipper_name',
        title: 'Shipper',
        width: '130px',
        minWidth: '130px',
        isUserSortable: true,
        cellContent: ({ row }) =>
          row.shipper_id !== null ? (
            <Link to={Routes.Company.generatePath({ company_id: row.shipper_id })}>{row.shipper_name}</Link>
          ) : (
            <>-</>
          ),
      },
      {
        id: 'carrier_name',
        title: 'Carrier',
        width: '130px',
        minWidth: '130px',
        isUserSortable: true,
        cellContent: ({ row }) =>
          row.carrier_id !== null ? (
            <Link to={Routes.Company.generatePath({ company_id: row.carrier_id })}>{row.carrier_name}</Link>
          ) : (
            <>-</>
          ),
      },
      {
        id: 'vehicles',
        title: 'Vehicle(s)',
        width: '160px',
        minWidth: '160px',
        cellContent: ({ row }) => (
          <>
            {row.vehicles.map(({ vehicle_id, license_plate_number }) => (
              <React.Fragment key={vehicle_id}>
                {license_plate_number !== null ? (
                  <Link to={Routes.Vehicle.generatePath({ vehicle_id })}>{license_plate_number}</Link>
                ) : (
                  <i>ID#{vehicle_id}</i>
                )}
                &nbsp;
              </React.Fragment>
            ))}
          </>
        ),
      },
      {
        id: 'shipper_transport_number',
        title: 'Shipper Transport No',
        width: '160px',
        minWidth: '160px',
        cellContent: ({ row }) => <>{row.shipper_transport_number}</>,
      },
      {
        id: 'transport_id',
        title: 'Transport ID',
        width: '200px',
        minWidth: '200px',
        cellContent: ({ row }) => <>{row.transport_id ?? '-'}</>,
      },
      {
        id: 'platform_name',
        title: 'Platform',
        width: '100px',
        minWidth: '100px',
        cellContent: ({ row }) => <>{row.platform_name}</>,
      },
      {
        id: 'start_time',
        title: 'Start time',
        width: '150px',
        minWidth: '150px',
        isUserSortable: true,
        cellContent: ({ row }) => <>{row.start_time}</>,
      },
      {
        id: 'tags',
        title: 'Tags',
        width: '80px',
        minWidth: '80px',
        cellContent: ({ row }) => <Tags tags={row.tags} />,
      },
      {
        id: 'tpaProductionUrl',
        title: 'Links',
        width: '120px',
        minWidth: '120px',
        cellContent: ({ row }) => {
          const mode = getMode(row.visibility_services);
          const tpaButton = createTpaButton(row, 'tpaProductionUrl', 'TPA UI');
          const tpaTestButton = createTpaButton(row, 'tpaTestUrl', 'TPA UI - AC');
          const tpaIntButton = createTpaButton(row, 'tpaIntUrl', 'TPA UI - IN');
          const tpaDevButton = createTpaButton(row, 'tpaDevUrl', 'TPA UI - DV');

          return row.transport_id !== null ? (
            <>
              {row.shipper_id !== null ? (
                <a
                  target="_blank"
                  title="Shipper"
                  href={shipmentViewUrl(row.shipper_id, row.tour_id.toString(), mode)}
                  rel="noreferrer">
                  📦
                </a>
              ) : (
                <span>No shipper ID</span>
              )}
              &nbsp;
              {row.carrier_id !== null ? (
                <a
                  target="_blank"
                  title="Carrier"
                  href={shipmentViewUrl(row.carrier_id, row.tour_id.toString(), mode)}
                  rel="noreferrer">
                  🚛
                </a>
              ) : (
                <span>No carrier ID</span>
              )}
              &nbsp;
              {tpaButton}
              {tpaTestButton}
              {tpaIntButton}
              {tpaDevButton}
              {row.isMobile && (
                <Link to={Routes.MobileTour.generatePath({ tour_id: row.tour_id.toString() })}>
                  <Tooltip content="Open in Mobile Command Center">
                    <Icon name="small-mobile" size={16} />
                  </Tooltip>
                </Link>
              )}
            </>
          ) : (
            <>No transport ID</>
          );
        },
      },
    ],
    [],
  );

  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  const containerRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (node === null) return;
    let el: HTMLElement | null = node.parentElement;
    while (el !== null) {
      const { overflow, overflowY } = getComputedStyle(el);
      if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
        setScrollParent(el);
        return;
      }
      el = el.parentElement;
    }
  }, []);

  return (
    <div ref={containerRefCallback}>
      {scrollParent !== null && (
        <InfiniteTable<FormattedTour>
          columns={columns}
          data={formattedTours}
          sortBy={sortBy}
          onSortByChange={onSortChange}
          scrollParent={scrollParent}
          className="compact-table"
        />
      )}
    </div>
  );
};
